import type { OrgBlock, OrgSeat, SeatHold, SeatKind } from './types';

// Mint-HR · Cơ cấu & ghế · Cây tổ chức (board Mint-HR, khối `otr`). Dàn ngang BOD → Khối → Phòng → Ghế; chỉ tính toạ độ, không đụng DOM.

export const SH = 56; // cao thẻ ghế
const SG = 8;
const BH = 52;
const DH = 52;
const GD = 12;
const GBK = 22;
const PAD = 16;
const CMIN = 216;

export interface OtSeat {
  seat: OrgSeat;
  holders: readonly SeatHold[];
  fill: number;
  vacant: number;
  hit: boolean;
}
export interface OtNode {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  color: string;
  seats: number;
  vacant: number;
  open: boolean;
  kids: number;
  root: boolean;
}
export interface OtCard extends OtSeat {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
}
export interface OtPanel {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
}
export interface OtLayout {
  width: number;
  height: number;
  links: ReadonlyArray<{ d: string; color: string | null }>;
  nodes: readonly OtNode[];
  cards: readonly OtCard[];
  panels: readonly OtPanel[];
  rootBox: { x: number; y: number; w: number; h: number };
  hits: readonly OtCard[];
  /** mọi khoá Phòng (để "Thu gọn hết") */
  deptKeys: readonly string[];
}

const BLOCK_COLORS = ['var(--d-blue)', 'var(--d-green)', 'var(--d-violet)'] as const;

/** "8 tài xế" = nhóm gộp: đếm theo số đầu. */
export function holderCount(code: string): number {
  const m = /^(\d+)\s/.exec(code);
  return m ? Number(m[1]) : 1;
}

export function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
}

const holderInfo = (seat: OrgSeat, q: string): OtSeat => {
  const fill = seat.holders.reduce((n, h) => n + holderCount(h.code), 0);
  const vacant = Math.max(0, seat.plan - fill);
  const hit = !!q && norm([seat.code, seat.name, ...seat.holders.map((h) => h.code)].join(' ')).includes(q);
  return { seat, holders: seat.holders, fill, vacant, hit };
};

const sum = (l: readonly OtSeat[]) => ({ n: l.reduce((a, x) => a + x.seat.plan, 0), m: l.reduce((a, x) => a + x.vacant, 0) });

/** Dàn cây. `vw` = bề rộng khung; `collapsed` = khoá đã thu gọn ('root' · 'b0' · 'd0-1'); `query` đã chuẩn hoá. */
export function layoutOrgTree(blocks: readonly OrgBlock[], vw: number, collapsed: ReadonlySet<string>, query: string): OtLayout {
  const q = norm(query).trim();
  const nar = vw < 1200;
  const RX = PAD;
  const RW = nar ? 200 : 228;
  const BX = RX + RW + (nar ? 30 : 40);
  const BW = nar ? 156 : 184;
  const DX = BX + BW + (nar ? 30 : 40);
  const DW = nar ? 184 : 208;
  const PX0 = DX + DW + (nar ? 22 : 28);
  const PW = vw - PAD - PX0;

  const bod = blocks.flatMap((b) => b.depts).find((d) => /\(BOD\)/.test(d.name));
  const rootSeats = (bod ? bod.seats : []).map((s) => holderInfo(s, q));
  const B = blocks.map((b, bi) => ({
    name: b.name,
    key: `b${bi}`,
    color: BLOCK_COLORS[bi % 3]!,
    open: true,
    y: 0,
    mid: 0,
    h: 0,
    depts: b.depts.filter((d) => d !== bod).map((d, di) => ({ name: d.name, key: `d${bi}-${di}`, open: true, y: 0, mid: 0, h: 0, seats: d.seats.map((s) => holderInfo(s, q)) })),
  }));
  const col = new Set(collapsed);
  // đang tìm: mở các nhánh có kết quả
  for (const b of B) for (const d of b.depts) if (d.seats.some((x) => x.hit)) { col.delete(d.key); col.delete(b.key); col.delete('root'); }

  const SC = Math.max(1, Math.floor((PW - 16 + SG) / (CMIN + SG)));
  const CWd = Math.floor((PW - 16 - (SC - 1) * SG) / SC);
  const rootOpen = !col.has('root');
  for (const b of B) {
    b.open = !col.has(b.key);
    for (const d of b.depts) {
      d.open = !col.has(d.key);
      const rows = Math.ceil(d.seats.length / SC);
      d.h = d.open && rows ? Math.max(DH, 16 + rows * SH + (rows - 1) * SG) : DH;
    }
    b.h = b.open && b.depts.length ? b.depts.reduce((a, d) => a + d.h, 0) + (b.depts.length - 1) * GD : BH;
  }
  const TH = rootOpen ? B.reduce((a, b) => a + b.h, 0) + (B.length - 1) * GBK : 0;
  const RH = 44 + rootSeats.length * (SH + SG) + 4;
  const height = PAD * 2 + Math.max(TH, RH) + 4;
  const ry = PAD + Math.max(0, (TH - RH) / 2);

  const links: Array<{ d: string; color: string | null }> = [];
  const nodes: OtNode[] = [];
  const cards: OtCard[] = [];
  const panels: OtPanel[] = [];
  const L = (d: string, color: string | null = null) => links.push({ d, color });
  const card = (x: number, y: number, w: number, it: OtSeat, color: string) => cards.push({ ...it, x, y, w, h: SH, color });

  const rs = sum(rootSeats);
  nodes.push({ key: 'root', x: RX, y: ry, w: RW, h: 40, label: bod ? bod.name : 'Ban giám đốc', color: 'var(--pri)', seats: rs.n, vacant: rs.m, open: rootOpen, kids: B.length, root: true });
  rootSeats.forEach((it, j) => card(RX + 8, ry + 44 + j * (SH + SG), RW - 16, it, 'var(--pri)'));

  if (rootOpen && B.length) {
    let y = PAD;
    const rm = ry + 20;
    const bx1 = RX + RW + 20;
    for (const b of B) { b.y = y; b.mid = y + b.h / 2; y += b.h + GBK; }
    L(`M${RX + RW} ${rm}H${bx1} M${bx1} ${Math.min(rm, B[0]!.mid)}V${Math.max(rm, B[B.length - 1]!.mid)}`);
    for (const b of B) {
      L(`M${bx1} ${b.mid}H${BX}`, b.color);
      const bs = sum(b.depts.flatMap((d) => d.seats));
      nodes.push({ key: b.key, x: BX, y: b.mid - BH / 2, w: BW, h: BH, label: b.name, color: b.color, seats: bs.n, vacant: bs.m, open: b.open, kids: b.depts.length, root: false });
      if (!b.open || !b.depts.length) continue;
      let dy = b.y;
      const dx1 = BX + BW + 20;
      for (const d of b.depts) { d.mid = dy + d.h / 2; d.y = dy; dy += d.h + GD; }
      L(`M${BX + BW} ${b.mid}H${dx1} M${dx1} ${Math.min(b.mid, b.depts[0]!.mid)}V${Math.max(b.mid, b.depts[b.depts.length - 1]!.mid)}`, b.color);
      for (const d of b.depts) {
        L(`M${dx1} ${d.mid}H${DX}`, b.color);
        const ds = sum(d.seats);
        nodes.push({ key: d.key, x: DX, y: d.mid - DH / 2, w: DW, h: DH, label: d.name, color: b.color, seats: ds.n, vacant: ds.m, open: d.open, kids: d.seats.length, root: false });
        if (!d.open || !d.seats.length) continue;
        L(`M${DX + DW} ${d.mid}H${PX0}`, b.color);
        panels.push({ x: PX0, y: d.y, w: PW, h: d.h, color: b.color });
        d.seats.forEach((it, si) => card(PX0 + 8 + (si % SC) * (CWd + SG), d.y + 8 + Math.floor(si / SC) * (SH + SG), CWd, it, b.color));
      }
    }
  }
  return {
    width: vw, height, links, nodes, cards, panels, rootBox: { x: RX, y: ry, w: RW, h: RH },
    hits: cards.filter((c) => c.hit),
    deptKeys: B.flatMap((b) => b.depts.map((d) => d.key)),
  };
}

export const SEAT_KIND_TONE: Record<SeatKind, 'mute' | 'warn' | 'pri'> = { Primary: 'mute', Concurrent: 'warn', Acting: 'pri' };

/** Zoom 50–150%, bước 10%, làm tròn 2 số. */
export const clampZoom = (z: number): number => Math.round(Math.max(0.5, Math.min(1.5, z)) * 100) / 100;

/** Zoom "vừa khung": thu cả cây vào khung (không phóng quá 100%). */
export function fitZoom(vw: number, vh: number, cw: number, ch: number): number {
  return Math.max(0.5, Math.min(1, Math.floor(Math.min(vw / cw, vh / ch) * 100) / 100));
}
